import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { Link } from 'react-router-dom';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import { FcDashboardClient } from '@/infrastructure/FcDashboardClient';
import type {
  FcDashboardCard,
  FcDashboardHeaderComments,
  FcDashboardStale,
  FcDashboardToday,
} from '@/models/FcDashboard';
import { ROUTES } from '@/routes/routes';
import { describeFailure } from '@/utils/fulfillmentErrors';
import { instantMs, kstDay, kstMinute } from '@/utils/fulfillmentTime';
import {
  BoxIcon,
  ClockIcon,
  ListIcon,
  RefreshIcon,
  ScanIcon,
  TruckIcon,
  WarningIcon,
} from '@/pages/fulfillment/components/UltimateIcons';
import { FCD_TEXT, useFcdLang, useFcdText, type FcdLang } from './fcdText';

/**
 * How often an open Today tab reads the counts again. The numbers are a few reads on the server and the day cards count
 * a day that is already over, so a quarter of an hour keeps the waiting lists current without loading the server.
 */
export const REFRESH_MS = 15 * 60 * 1000;

const count = (value: number): string => value.toLocaleString('en-US');

/** How long an order has waited, measured against the server's clock so a wrong desk clock cannot move it. */
export const waitedLabel = (placedAt: string, asOf: string, lang: FcdLang = 'en'): string => {
  const [placed, now] = [instantMs(placedAt), instantMs(asOf)];
  const waited = placed === null || now === null ? Number.NaN : now - placed;
  if (!Number.isFinite(waited) || waited < 0) {
    return '—';
  }
  const hours = Math.floor(waited / 3_600_000);
  return FCD_TEXT[lang].waited(Math.floor(hours / 24), hours % 24);
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** The KST day before the one `now` falls on: the latest day the day cards may count, since today is not over. */
export const kstYesterday = (now: Date): string => {
  const todayStart = Date.parse(`${kstDay(now.toISOString())}T00:00:00+09:00`);
  return kstDay(new Date(todayStart - DAY_MS).toISOString());
};

const WEEKDAYS: Record<FcdLang, readonly string[]> = {
  ko: ['일', '월', '화', '수', '목', '금', '토'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
};

/** `10/20 (Tue)` for a `2026-10-20` KST day. */
const dayLabel = (day: string, lang: FcdLang): string => {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  return `${day.slice(5).replace('-', '/')} (${WEEKDAYS[lang][weekday]})`;
};

/** The store status of a waiting order, in the chip colour the desk screens use for it. */
const WC_CHIP: Record<string, string> = {
  processing: 'status st-processing',
  'prepare-shipment': 'status st-prepare',
  completed: 'status st-completed',
  shipped: 'status st-completed',
  'on-hold': 'status st-hold',
  cancelled: 'status st-cancelled',
  refunded: 'status st-cancelled',
  failed: 'status st-cancelled',
};
const wcChipClass = (status: string): string => WC_CHIP[status.replace(/^wc-/, '')] ?? 'status fcd-wc';

type TodayState =
  | { kind: 'loading' }
  | { kind: 'ready'; today: FcDashboardToday }
  | { kind: 'failed'; message: string };

/** The read that failed and when. */
interface ReadFailure {
  message: string;
  at: string;
}

type WaitingList = 'ready' | 'awaiting';

interface TileProps {
  label: string;
  icon: ReactNode;
  card: FcDashboardCard<unknown>;
  /** The time window above the name, for a tile counted against a threshold. */
  qualifier?: string;
  /** Amber edge: a count of orders that have waited too long. */
  attention?: boolean;
  /** Opens this tile's list below: the tile's name becomes the way in. */
  onOpen?: () => void;
  /** What the number counts, at the foot of the tile. */
  basis: string;
  children?: ReactNode;
}

/** One number tile; a card that failed says so in place of a value, never as a zero. */
function Tile({ label, icon, card, qualifier, attention, onOpen, basis, children }: TileProps) {
  const text = useFcdText();
  const className = ['fcd-tile', card.status === 'ok' ? '' : 'is-error', attention ? 'is-attention' : '']
    .filter(Boolean)
    .join(' ');
  const name = (
    <>
      <span className="fcd-tile-icon">{icon}</span>
      <span className="fcd-tile-name">{label}</span>
    </>
  );
  return (
    <section className={className} aria-label={label}>
      {qualifier && <div className="fcd-tile-q">{qualifier}</div>}
      {onOpen ? (
        <button
          type="button"
          className="fcd-tile-k is-link"
          aria-label={`${label} · ${text.viewList}`}
          onClick={onOpen}
        >
          {name}
          <span className="fcd-tile-go" aria-hidden="true">
            ›
          </span>
        </button>
      ) : (
        <div className="fcd-tile-k">{name}</div>
      )}
      {card.status === 'ok' ? (
        children
      ) : (
        <div className="fcd-tile-err">
          <WarningIcon />
          <span>{text.couldNotRead(card.error ?? 'unknown error')}</span>
        </div>
      )}
      <div className="fcd-tile-fill" />
      <div className="fcd-tile-basis">{basis}</div>
    </section>
  );
}

function Value({ value, unit }: { value: number; unit: string }) {
  return (
    <div className="fcd-tile-v">
      {count(value)} <small>{unit}</small>
    </div>
  );
}

interface WaitingListProps {
  list: WaitingList;
  onList: (next: WaitingList) => void;
  today: FcDashboardToday;
  listRef: RefObject<HTMLElement>;
}

/** The two waiting lists behind one switch, oldest first. */
function WaitingOrders({ list, onList, today, listRef }: WaitingListProps) {
  const text = useFcdText();
  const lang = useFcdLang();
  type ListEntry = { key: WaitingList; label: string; card: FcDashboardCard<FcDashboardStale>; empty: string };
  const byKey: Record<WaitingList, ListEntry> = {
    ready: { key: 'ready', label: text.ready, card: today.readyStale, empty: text.readyEmpty },
    awaiting: { key: 'awaiting', label: text.awaiting, card: today.awaitingInboundStale, empty: text.awaitingEmpty },
  };
  const lists: readonly ListEntry[] = [byKey.ready, byKey.awaiting];
  const chosen = byKey[list];
  const stale = chosen.card.value;
  return (
    <section className="fcd-block" aria-label={text.waitingList} ref={listRef} tabIndex={-1}>
      <div className="psub">
        <h3>{text.waitingList}</h3>
        <span className="sp" />
        {stale && stale.total > stale.orders.length && (
          <span className="fcd-note">{text.oldestOf(stale.orders.length, stale.total)}</span>
        )}
      </div>
      <div className="viewtabs fcd-listtabs" role="tablist" aria-label={text.listChoice}>
        {lists.map((entry) => (
          <button
            type="button"
            key={entry.key}
            role="tab"
            id={`fcd-list-${entry.key}`}
            aria-controls="fcd-list-panel"
            aria-selected={entry.key === list}
            className={entry.key === list ? 'vtab on' : 'vtab'}
            onClick={() => onList(entry.key)}
          >
            {entry.label}{' '}
            <span className="n">
              {entry.card.status === 'ok' && entry.card.value ? count(entry.card.value.total) : '—'}
            </span>
          </button>
        ))}
      </div>
      <div role="tabpanel" id="fcd-list-panel" aria-labelledby={`fcd-list-${list}`}>
        {chosen.card.status !== 'ok' || !stale ? (
          <div className="fcd-state is-error" role="alert">
            <WarningIcon />
            <span>{text.couldNotRead(chosen.card.error ?? 'unknown error')}</span>
          </div>
        ) : (
          <div className="tbl-scroll">
            <table className="tbl fcd-stale" aria-label={chosen.label}>
              <colgroup>
                <col className="col-order" />
                <col className="col-placed" />
                <col className="col-waited" />
                <col className="col-pic" />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th>{text.colOrder}</th>
                  <th>{text.colPlaced}</th>
                  <th>{text.colSince}</th>
                  <th>{text.colPic}</th>
                  <th>{text.colStatus}</th>
                </tr>
              </thead>
              <tbody>
                {stale.orders.length === 0 && (
                  <tr>
                    <td colSpan={5} className="fcd-empty">
                      {chosen.empty}
                    </td>
                  </tr>
                )}
                {stale.orders.map((order) => (
                  <tr key={order.orderId}>
                    <td>
                      <Link className="oid" to={ROUTES.fulfillment.orderDetail(order.orderId)}>
                        #{order.orderId}
                      </Link>
                    </td>
                    <td className="num">{kstMinute(order.orderPlacedAt)}</td>
                    <td className="num fcd-waited">{waitedLabel(order.orderPlacedAt, today.generatedAt, lang)}</td>
                    <td>{order.picUser ?? '—'}</td>
                    <td>
                      <span className="fcd-flags">
                        {order.wcStatus && <span className={wcChipClass(order.wcStatus)}>{order.wcStatus}</span>}
                        {order.onHold && <span className="status st-hold">{text.onHold}</span>}
                        {order.isMarketing && <span className="status fcd-mkt">{text.marketing}</span>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function HeaderComments({ card }: { card: FcDashboardCard<FcDashboardHeaderComments> }) {
  const text = useFcdText();
  const headed = card.value;
  const title = headed ? text.headed(headed.month) : text.headedThisMonth;
  return (
    <section className="fcd-block" aria-label={title}>
      <div className="psub">
        <h3>{title}</h3>
        <span className="sp" />
        <span className="fcd-note">{text.headedRule}</span>
      </div>
      {card.status !== 'ok' || !headed ? (
        <div className="fcd-state is-error" role="alert">
          <WarningIcon />
          <span>{text.couldNotRead(card.error ?? 'unknown error')}</span>
        </div>
      ) : (
        <>
          <div className="fcd-headers" aria-label={text.colHeaders}>
            <div className="fcd-headers-total">
              <span>{text.headedTotal}</span>
              <b>{count(headed.total)}</b>
            </div>
            {(
              [
                ['UNRECOGNIZED', headed.byHeader.unrecognized],
                ['INBOUND_ANOMALY', headed.byHeader.inboundAnomaly],
                ['REORDER', headed.byHeader.reorder],
                ['STOCK_MISMATCH', headed.byHeader.stockMismatch],
              ] as const
            ).map(([header, value]) => (
              <div key={header} className={value > 0 ? 'fcd-headers-item has' : 'fcd-headers-item'}>
                <span>{text.header[header]}</span>
                <b>{count(value)}</b>
              </div>
            ))}
          </div>
          {!headed.complete && (
            <div className="fcd-state" role="note">
              <WarningIcon />
              <span>{headed.unplaced > 0 ? text.headedShortUnplaced(headed.unplaced) : text.headedShortSearch}</span>
            </div>
          )}
          <div className="tbl-scroll">
            <table className="tbl fcd-comments" aria-label={title}>
              <colgroup>
                <col className="col-ref" />
                <col className="col-when" />
                <col className="col-headers" />
                <col className="col-author" />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th>{text.colRef}</th>
                  <th>{text.colWhen}</th>
                  <th>{text.colHeaders}</th>
                  <th>{text.colBy}</th>
                  <th>{text.colComment}</th>
                </tr>
              </thead>
              <tbody>
                {headed.comments.length === 0 && (
                  <tr>
                    <td colSpan={5} className="fcd-empty">
                      {text.headedEmpty}
                    </td>
                  </tr>
                )}
                {headed.comments.map((comment) => (
                  <tr key={`${comment.orderId ?? comment.docId}#${comment.commentedAt}`}>
                    <td>
                      {comment.orderId !== null ? (
                        <Link className="oid" to={ROUTES.fulfillment.orderDetail(comment.orderId)}>
                          #{comment.orderId}
                        </Link>
                      ) : comment.docId ? (
                        <Link className="oid" to={ROUTES.fulfillment.inboundRequestDetail(comment.docId)}>
                          {comment.inboundNo ?? comment.docId}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="num">{kstMinute(comment.commentedAt)}</td>
                    <td>
                      <span className="fcd-flags">
                        {comment.headers.map((header) => (
                          <span key={header} className="status st-hold">
                            {text.header[header]}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td>{comment.author ?? '—'}</td>
                    <td>
                      <div className="fcd-text">{comment.text}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * The Today tab (SSD-7602): the day's throughput (Inbound, Outbound, Handover), the orders that have waited too long,
 * and this month's headed comments. It reads once on open, again every quarter of an hour while the browser tab is in
 * view, and on Refresh.
 */
export function TodayTab() {
  const text = useFcdText();
  const lang = useFcdLang();
  const client = useMemo(() => new FcDashboardClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  /** The KST day the day cards count; null leaves the choice to the server, which takes yesterday. */
  const [date, setDate] = useState<string | null>(null);
  const [state, setState] = useState<TodayState>({ kind: 'loading' });
  const [reading, setReading] = useState(false);
  /** A read that failed while earlier numbers stay on screen: said, so old numbers never pass for current ones. */
  const [readFailure, setReadFailure] = useState<ReadFailure | null>(null);
  const [list, setList] = useState<WaitingList>('ready');
  const listRef = useRef<HTMLElement>(null);
  const requestRef = useRef(0);
  const lastReadRef = useRef(0);
  const dateRef = useRef<string | null>(null);
  dateRef.current = date;
  /** The failure wording follows the language without the read itself depending on it. */
  const failedTextRef = useRef(text.readFailed);
  failedTextRef.current = text.readFailed;

  const load = useCallback(
    async (day: string | null) => {
      const request = ++requestRef.current;
      setReading(true);
      try {
        const today = await client.getToday(day ?? undefined);
        if (request === requestRef.current) {
          lastReadRef.current = Date.now();
          setState({ kind: 'ready', today });
          setReadFailure(null);
        }
      } catch (error) {
        if (request === requestRef.current) {
          const message = describeFailure(error, failedTextRef.current);
          // The numbers already on screen stay, with the failure said above them; a first read that failed shows it alone.
          setState((current) => (current.kind === 'ready' ? current : { kind: 'failed', message }));
          setReadFailure({ message, at: new Date().toISOString() });
        }
      } finally {
        if (request === requestRef.current) {
          setReading(false);
        }
      }
    },
    [client],
  );

  useEffect(() => {
    void load(date);
  }, [load, date]);

  // Every quarter of an hour while the tab is in view; a tab coming back after longer than that reads at once.
  useEffect(() => {
    const visible = () => document.visibilityState === 'visible';
    const timer = window.setInterval(() => {
      if (visible()) {
        void load(dateRef.current);
      }
    }, REFRESH_MS);
    const onVisibility = () => {
      if (visible() && Date.now() - lastReadRef.current >= REFRESH_MS) {
        void load(dateRef.current);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load]);

  const openList = (next: WaitingList) => {
    setList(next);
    listRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const latestDay = kstYesterday(new Date());

  if (state.kind === 'loading') {
    return (
      <div className="fcd-state" role="status">
        <ClockIcon />
        <span>{text.reading}</span>
      </div>
    );
  }
  if (state.kind === 'failed') {
    return (
      <div className="fcd-state is-error" role="alert">
        <WarningIcon />
        <span>{state.message}</span>
        <button type="button" className="btn btn-sm btn-line" onClick={() => void load(date)}>
          {text.tryAgain}
        </button>
      </div>
    );
  }

  const { today } = state;
  const day = dayLabel(today.date, lang);
  const inbound = today.inbound.value;
  const outbound = today.outbound.value;
  const scans = today.closingScans.value;
  const ready = today.readyStale.value;
  const awaiting = today.awaitingInboundStale.value;
  const snapshot = kstMinute(today.generatedAt);

  return (
    <div className="fcd-today">
      <div className="fcd-bar">
        <label className="fcd-day">
          <span>{text.day}</span>
          <input
            type="date"
            aria-label={text.dayInput}
            value={date ?? today.date}
            max={latestDay}
            onChange={(event) => {
              const picked = event.target.value;
              // A typed day past yesterday is not over yet and would read as final; it is ignored.
              if (!picked) {
                setDate(null);
              } else if (picked <= latestDay) {
                setDate(picked);
              }
            }}
          />
        </label>
        <button type="button" className="btn btn-line" disabled={date === null} onClick={() => setDate(null)}>
          {text.yesterday}
        </button>
        <span className="fcd-meta">
          <ClockIcon />
          <span>
            <b>{text.asOf(snapshot)}</b> · {text.everyQuarter}
          </span>
        </span>
        <span className="fcd-chip">{text.calendarChip}</span>
        <span className="sp" />
        <button type="button" className="btn btn-blue" disabled={reading} onClick={() => void load(date)}>
          <RefreshIcon />
          {reading ? text.reading : text.refresh}
        </button>
      </div>
      {readFailure && (
        <div className="fcd-state is-error" role="alert">
          <WarningIcon />
          <span>{text.laterReadFailed(kstMinute(readFailure.at), readFailure.message, snapshot, today.date)}</span>
        </div>
      )}

      <div className="fcd-groups">
        <section className="fcd-group" aria-label={text.throughput(day)}>
          <div className="fcd-group-head">
            <h3>{text.throughput(day)}</h3>
            <span className="fcd-group-note">{text.throughputNote}</span>
          </div>
          <div className="fcd-tiles fcd-tiles-3">
            <Tile label={text.inbound} icon={<BoxIcon />} card={today.inbound} basis={text.inboundBasis}>
              {inbound && (
                <>
                  <Value value={inbound.units} unit={text.units} />
                  <div className="fcd-tile-s">{text.inboundSub(inbound.orders, inbound.cancelledUnits)}</div>
                  {inbound.requestUnits !== 0 && (
                    <div className="fcd-tile-s">{text.inboundRequests(inbound.requestUnits, inbound.requests)}</div>
                  )}
                </>
              )}
            </Tile>
            <Tile label={text.outbound} icon={<TruckIcon />} card={today.outbound} basis={text.outboundBasis}>
              {outbound && (
                <>
                  <Value value={outbound.orders} unit={text.orders} />
                  <div className="fcd-tile-s">{text.outboundSub(outbound.units, outbound.cancelledUnits)}</div>
                </>
              )}
            </Tile>
            <Tile label={text.handover} icon={<ScanIcon />} card={today.closingScans} basis={text.handoverBasis}>
              {scans && (
                <>
                  <Value value={scans.boxes} unit={text.boxes} />
                  <div className="fcd-tile-s">{text.handoverSub}</div>
                  {scans.boxesOnlyInCancelledSessions > 0 && (
                    <div className="fcd-tile-s fcd-warn">
                      {text.handoverCancelled(scans.boxesOnlyInCancelledSessions)}
                    </div>
                  )}
                </>
              )}
            </Tile>
          </div>
        </section>

        <section className="fcd-group" aria-label={text.waitingNow}>
          <div className="fcd-group-head">
            <h3>{text.waitingNow}</h3>
            <span className="fcd-group-note">{text.waitingNote(snapshot)}</span>
          </div>
          <div className="fcd-tiles fcd-tiles-2">
            <Tile
              label={text.ready}
              icon={<ListIcon />}
              qualifier={text.readyQualifier}
              card={today.readyStale}
              attention={(ready?.total ?? 0) > 0}
              onOpen={() => openList('ready')}
              basis={text.readyBasis}
            >
              {ready && (
                <>
                  <Value value={ready.total} unit={text.orders} />
                  <div className="fcd-tile-s">{text.readySub(ready.onHold, ready.marketing)}</div>
                </>
              )}
            </Tile>
            <Tile
              label={text.awaiting}
              icon={<ClockIcon />}
              qualifier={text.awaitingQualifier}
              card={today.awaitingInboundStale}
              attention={(awaiting?.total ?? 0) > 0}
              onOpen={() => openList('awaiting')}
              basis={text.awaitingBasis}
            >
              {awaiting && (
                <>
                  <Value value={awaiting.total} unit={text.orders} />
                  <div className="fcd-tile-s">
                    {text.awaitingSub(awaiting.total - awaiting.marketing, awaiting.marketing)}
                  </div>
                </>
              )}
            </Tile>
          </div>
        </section>
      </div>

      <WaitingOrders list={list} onList={setList} today={today} listRef={listRef} />
      <HeaderComments card={today.headerComments} />
    </div>
  );
}
